<template>
    <div class="relative font-sans min-w-[240px]">
        <vx-selectable
            :modelValue="selectedRange.value"
            @update:modelValue="handleControlDateRangeOption"
            :options="options"
            :reduce="(option) => option.value"
            label="label"
            :clearable="false"
            size="lg"
        ></vx-selectable>
        <transition
            enter-from-class="opacity-0 -translate-y-4"
            enter-to-class="opacity-100 translate-y-0"
            leave-from-class="opacity-100 translate-y-0"
            leave-to-class="opacity-0 -translate-y-4"
        >
            <div v-if="isDatePickerOpened" class="absolute z-20 mb-8 inset-e-0 transition" @click.stop>
                <vx-date-picker
                    mode="date"
                    :modelValue="datePickerRangeModel"
                    @update:modelValue="handleDatePickerRange"
                    is-range
                    v-on-click-outside="() => closeDatePickerRange()"
                    :timezone="getClientTz()"
                    :maxDate="maxCustomDate"
                    :minDate="minCustomDate"
                    @dayclick="handleDayClick"
                />
            </div>
        </transition>
    </div>
</template>

<script setup>
import { ref, computed, nextTick, watch } from 'vue';
import { VxSelectable, VxDatePicker } from '@voxie/frontend-components';
import { vOnClickOutside } from '@vueuse/components';
import {
    controlDateRangeOptions,
    formatDateForRange,
    getCurrentDate,
    getMinAllowedDate,
} from '../../../utils/analytics';
import { getClientTz, shortDate } from '../../../utils/date';
import dayjs from '~/utils/dayjs';

const emit = defineEmits(['update:modelValue']);

const props = defineProps({
    modelValue: {
        type: Object,
        required: true,
    },
    orgUnitId: {
        type: Number,
    },
    groupId: {
        type: String,
    },
});

// dayjs calculates an end date starting from a next day. in order to get the right range including a current day we decrease max days by 1.
const MAX_DAYS_RANGE = 99;

const clickedDay = ref(null);

const handleDayClick = (event) => {
    if (!clickedDay.value) {
        clickedDay.value = event.date;
    }
};

const minAllowedDate = computed(() => getMinAllowedDate(Boolean(props.groupId), Boolean(props.orgUnitId)));

const maxAllowed = computed(() => {
    const currentDate = getCurrentDate();

    // current day is only available if the time is at least 01:30 local.
    if (currentDate.isAfter(currentDate.clone().set('hour', 1).set('minutes', 30))) {
        return currentDate.toDate();
    }

    return currentDate.subtract(1, 'day').endOf('day').toDate();
});

const maxCustomDate = computed(() => {
    if (clickedDay.value && dayjs(clickedDay.value).add(MAX_DAYS_RANGE, 'days').isAfter(maxAllowed.value)) {
        return maxAllowed.value;
    }

    if (clickedDay.value) {
        return dayjs(clickedDay.value).add(MAX_DAYS_RANGE, 'days').toDate();
    }

    return maxAllowed.value;
});

const minCustomDate = computed(() => {
    if (clickedDay.value && dayjs(clickedDay.value).subtract(MAX_DAYS_RANGE, 'days').isBefore(minAllowedDate.value)) {
        return minAllowedDate.value.toDate();
    }

    if (clickedDay.value) {
        return dayjs(clickedDay.value).subtract(MAX_DAYS_RANGE, 'days').toDate();
    }

    return minAllowedDate.value.toDate();
});

const customRangeOption = ref(null);
const options = computed(() => {
    const options = controlDateRangeOptions(Boolean(props.groupId), Boolean(props.orgUnitId));

    if (customRangeOption.value) {
        options.push({
            ...customRangeOption.value,
        });
    }

    return options;
});

// In order to get fresh dates on reset.
const defaultDatePickerRange = () => ({
    start: dayjs(maxCustomDate.value).tz(getClientTz()).subtract(2, 'days').startOf('day').toDate(),
    end: dayjs(maxCustomDate.value).tz(getClientTz()).startOf('day').toDate(),
});

const selectedRange = ref(props.modelValue);
const isDatePickerOpened = ref(false);
const datePickerRangeModel = ref(defaultDatePickerRange());

const handleControlDateRangeOption = (optionKey) => {
    const option = options.value.find((o) => o.value === optionKey);

    selectedRange.value = { ...option };

    if (optionKey === 'selected_custom') {
        return;
    }

    if (optionKey === 'custom') {
        isDatePickerOpened.value = true;
        customRangeOption.value = null;
        return;
    }

    customRangeOption.value = null;

    emit('update:modelValue', selectedRange.value);
};

const handleDatePickerRange = async ({ start, end }) => {
    selectedRange.value = customRangeOption.value;

    customRangeOption.value = {
        value: 'selected_custom',
        start_date: formatDateForRange(dayjs(start)),
        end_date: formatDateForRange(dayjs.min([getCurrentDate(), dayjs(end).add(1, 'day')])),
        label: `${shortDate(start)} - ${shortDate(end)}`,
    };

    emit('update:modelValue', customRangeOption.value);

    isDatePickerOpened.value = false;
    datePickerRangeModel.value = defaultDatePickerRange();
    await nextTick();
    clickedDay.value = null;
};

const closeDatePickerRange = () => {
    isDatePickerOpened.value = false;
    handleDatePickerRange(defaultDatePickerRange());
};

watch(
    () => props.modelValue,
    () => {
        selectedRange.value = props.modelValue;
        if (selectedRange.value.value === 'selected_custom') {
            customRangeOption.value = props.modelValue;
        }
    },
    { immediate: true }
);
</script>
